/**
 * ¿EL CUERPO DE ESTE DOCUMENTO TRAE FORMATO, O ES EL TEXTO PLANO DE SIEMPRE?
 *
 * Lissette, 2026-09-15: *"en firmas cuando se edite un template tener el mismo
 * sistema de como se envian los correos, es decir negrita, subrayado,
 * justificado, todo igual como se manda correo"*.
 *
 * Desde ese cambio el editor de plantillas es el de los correos y guarda HTML.
 * Pero las plantillas de antes son TEXTO PLANO, y hay documentos ya firmados con
 * ellas: no se migran, se distinguen. Un cuerpo con bloques se pinta como HTML;
 * uno sin ellos, con `white-space: pre-wrap`, que es lo que ha hecho siempre.
 *
 * TRES SITIOS enseñan un documento —la lista de plantillas, la vista previa con
 * los datos del destinatario, y la pagina donde el cliente lo firma— y el
 * servidor lo dibuja ademas en el PDF. Si cada uno decidiera por su cuenta, el
 * mismo documento se veria de una forma en la pantalla del agente y de otra en
 * la del cliente, que es la clase de desajuste que hace dudar de la firma.
 *
 * ESPEJO de `HAS_BLOCK_HTML` en `deskte-api/src/utils/emailPresentation.ts`, que
 * es quien lo decide del lado del servidor (`esDocumentoConFormato` en
 * `utils/documentoConFormato.ts`). Si cambia una, cambia la otra:
 * `tests/unit/documentoConFormato.test.ts` compara los dos ficheros.
 *
 * Y OJO con la tentacion de simplificarlo a "¿tiene alguna etiqueta?": las
 * etiquetas EN LINEA no cuentan a proposito. Un cuerpo de texto plano que lleve
 * pegado un `<strong>` sigue necesitando que sus saltos de linea se respeten, y
 * ese atajo fue justo el fallo de la presentacion del correo (una sola `<img>`
 * desactivaba la conversion de TODO el cuerpo).
 */

const BLOQUES_HTML = /<\s*(p|div|br|table|tr|td|ul|ol|li|h[1-6]|blockquote|section|article)\b/i;

/**
 * CUANTO PUEDE PESAR EL CUERPO DE UN DOCUMENTO DE FIRMA.
 *
 * ── DE DONDE SALE ESTE NUMERO ──────────────────────────────────────────────
 *
 * Crismar Perez (Maricarmen Nieto), 2026-09-17: creo una carta nueva para los
 * prospectos, la vista previa se veia bien, y al enviarla salia *"Invalid input
 * - Body text: String must contain at most 20000 character(s)"*.
 *
 * Los 20.000 se escribieron cuando el cuerpo de una plantilla era TEXTO PLANO.
 * El 2026-09-15 paso a ser HTML (#2095: *"en firmas... negrita, subrayado,
 * justificado, todo igual como se manda correo"*) y el tope se quedo pensando en
 * texto: un documento de dos paginas, que como texto no llega a 5.000, como HTML
 * se pasa sin ser largo. Nadie lo subio porque el tope no vive donde se hizo el
 * cambio.
 *
 * 100.000 caracteres de HTML LIMPIO —el que deja `limpiarHtmlPegado`, que
 * conserva los bloques y las cuatro marcas y tira lo demas— son del orden de 25
 * a 30 paginas de texto. Un documento que se firma de una sentada no pasa de
 * ahi, y lo que se pasa casi siempre es basura pegada, no contenido.
 *
 * NO SE QUITA EL TOPE, y eso es deliberado: este cuerpo se guarda, se manda por
 * correo, se pinta en la pagina publica de firma y se DIBUJA renglon a renglon
 * en un PDF. Sin tope, un pegado de 40 MB no da un error: da un PDF que tarda
 * minutos o una peticion que muere a medias.
 *
 * ESPEJO de `MAX_CUERPO_DOCUMENTO` en `deskte-api/src/utils/documentoConFormato.ts`,
 * que es quien lo hace cumplir (`signatures.routes.ts`). Si cambia una, cambia
 * la otra: `tests/unit/cuerpoDelDocumento.test.ts` compara los dos archivos. Con
 * el del navegador MAS BAJO que el del servidor no pasaria nada; al reves, la
 * pantalla diria que cabe y el envio lo rechazaria — que es el fallo de origen.
 */
export const MAX_CUERPO_DOCUMENTO = 100000;

/** ¿Este cuerpo se pinta como HTML? */
export function esDocumentoConFormato(cuerpo) {
  return BLOQUES_HTML.test(String(cuerpo ?? ''));
}

/**
 * El estilo con el que se pinta el cuerpo.
 *
 * Texto plano: `pre-wrap`, porque sus saltos de linea son los del documento.
 * HTML: nada — los saltos ya los deciden sus etiquetas, y dejar `pre-wrap`
 * duplicaria cada uno (el salto del fuente MAS el del `<p>`), que es el "todo a
 * doble espacio" de siempre.
 */
export function estiloDelCuerpo(cuerpo) {
  return esDocumentoConFormato(cuerpo) ? {} : { whiteSpace: 'pre-wrap' };
}

/**
 * LA FUENTE, EL TAMAÑO Y LA ALINEACIÓN que la plantilla tiene guardados.
 *
 * Los de fábrica son los de la hoja de siempre: una serif de carta a 15px
 * alineada a la izquierda. Vive aquí —y no dentro del componente de la hoja—
 * porque desde una prueba no se puede importar un `.jsx` (regla 48), y esto es
 * justo lo que se perdía en la vista previa del CRM: el documento salía
 * "movido, no centrado" porque el formato guardado no se aplicaba (E-317).
 */
export function formatoDelDocumento(formatting) {
  const fmt = (formatting && typeof formatting === 'object') ? formatting : {};
  return {
    font: fmt.font || '"Georgia", "Times New Roman", serif',
    fontSize: fmt.fontSize ? `${fmt.fontSize}px` : 15,
    align: fmt.align || 'left',
  };
}

/**
 * Un cuerpo de TEXTO PLANO convertido al HTML que el editor entiende.
 *
 * ── POR QUE `<div>` POR LINEA Y NO `<p>` POR PARRAFO ────────────────────────
 *
 * Porque tiene que verse IGUAL que antes en los tres sitios y en el PDF. El
 * texto plano se pintaba con `pre-wrap`: una linea por salto, las vacias
 * incluidas. Con `<p>` por parrafo se pierden las lineas en blanco (el PDF las
 * dibuja renglon a renglon, sin margenes, asi que el documento se comprime) y en
 * el navegador se ganan margenes que nadie pidio. Con un `<div>` por linea —y un
 * `<div><br></div>` para la vacia— sale exactamente lo mismo en los dos sitios.
 *
 * Y de paso es lo que produce el propio editor cuando alguien pulsa Enter, asi
 * que el documento no cambia de forma segun quien haya escrito cada trozo.
 *
 * Se usa al ABRIR una plantilla vieja en el editor y cada vez que algo mete
 * texto plano dentro (la traduccion del AI, el documento convertido de un PDF,
 * el ejemplo de fabrica). Un texto plano metido en un editor de HTML pierde sus
 * saltos de linea sin decir nada: el documento entero se queda en un parrafo.
 */
export function aHtmlDeDocumento(texto) {
  const t = String(texto ?? '');
  if (!t) return '';
  if (esDocumentoConFormato(t)) return t; // ya viene con formato: no se toca
  const escapar = (x) => x
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return t
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((linea) => (linea.trim() ? `<div>${escapar(linea)}</div>` : '<div><br></div>'))
    .join('');
}
